import React, { useState, useEffect, useCallback } from "react";
import { Icon } from "@iconify/react";
import useApi from "../../hook/useApi";
import { toast } from "react-toastify";
import Loader from "../Loader/Loader";
import { ROLES } from "../../utils/enum";
import SearchableSelect from "../Common/SearchableSelect";

const CashEntryLayer = () => {
  const { AsyncPostApiCall, AsyncGetApiCall } = useApi();
  const todayDate = new Date().toISOString().split("T")[0];

  const [actionType, setActionType] = useState("credit");
  const [userType, setUserType] = useState(ROLES.BROKER);
  const [user, setUser] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayDate);
  const [remarks, setRemarks] = useState("");
  const [userOptions, setUserOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadUserOptions = useCallback(async () => {
    setLoadingOptions(true);
    setUser("");
    try {
      let result;
      if (userType === ROLES.BROKER) {
        result = await AsyncGetApiCall("/broker", { pageNo: 1, limitVal: 500 });
      } else {
        result = await AsyncGetApiCall("/users/dynamic-list?listType=user", { pageNo: 1, limitVal: 500 });
      }

      console.log("result-->", result);

      const data = result?.success ? result.data : result || [];
      const list = Array.isArray(data) ? data : [];

      const options = list.map((item) => ({
        value: item._id,
        label: `${item.name || item.masterName} (${item.loginId})`,
      }));

      setUserOptions(options);
    } catch (e) {
      console.error("Failed to load accounts", e);
      setUserOptions([]);
      toast.error("Failed to load user list");
    } finally {
      setLoadingOptions(false);
    }
  }, [userType, AsyncGetApiCall]);

  useEffect(() => {
    loadUserOptions();
  }, [loadUserOptions]);

  const handleAmountUp = () => setAmount((prev) => prev + 1);
  const handleAmountDown = () => setAmount((prev) => Math.max(0, prev - 1));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.warning("Please select a user");
      return;
    }
    if (!date) {
      toast.warning("Please select a date");
      return;
    }
    if (amount <= 0) {
      toast.warning("Amount must be greater than 0");
      return;
    }

    setSubmitting(true);
    setLoading(true);

    try {
      const payload = {
        userId: user,
        userType,
        entryType: actionType,
        amount: Number(amount),
        date,
        remarks: remarks.trim(),
      };

      const res = await AsyncPostApiCall("/accounts/create-entry", payload); // Adjust endpoint if needed

      if (res?.success) {
        toast.success(res.message || "Cash entry created successfully");
        // Reset form
        setAmount(0);
        setRemarks("");
        setUser("");
        setDate(todayDate);
      } else {
        toast.error(res?.message || "Failed to create entry");
      }
    } catch (error) {
      const message = error.response?.data?.message || "Failed to create entry";
      toast.error(message);
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  return (
    <>
      {(loading || loadingOptions) && <Loader />}

      <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-3 mb-24">
        <div className="d-flex align-items-center gap-2 text-secondary-light">
          <Icon icon="tabler:layout-grid" className="icon text-xl" />
          <Icon icon="tabler:chevron-right" className="icon text-sm" />
          <span className="fw-semibold text-primary-light">Cash Entry</span>
        </div>
      </div>

      <div className="card border shadow-sm radius-16 mb-24">
        <div className="card-body p-24">
          <h6 className="fw-semibold mb-3">Create Cash Entry</h6>

          <form onSubmit={handleSubmit} className="row g-4">
            {/* Type / Action */}
            <div className="col-12">
              <label className="form-label fw-bold text-primary-light mb-3 d-block">
                Type / Action
              </label>
              <div className="d-flex flex-wrap gap-4">
                <label className="d-flex align-items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="actionType"
                    checked={actionType === "credit"}
                    onChange={() => setActionType("credit")}
                    className="form-check-input"
                  />
                  <span className="text-secondary-light">
                    Take Payment From / Credit / Receipt / Deposit
                  </span>
                </label>
                <label className="d-flex align-items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="actionType"
                    checked={actionType === "debit"}
                    onChange={() => setActionType("debit")}
                    className="form-check-input"
                  />
                  <span className="text-secondary-light">
                    Give Payment To / Debit / Payment / Withdraw
                  </span>
                </label>
              </div>
            </div>

            {/* User Type */}
            <div className="col-12">
              <label className="form-label fw-bold text-primary-light mb-3 d-block">
                User Type
              </label>
              <div className="d-flex flex-wrap gap-4">
                <label className="d-flex align-items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="userType"
                    checked={userType === ROLES.BROKER}
                    onChange={() => setUserType(ROLES.BROKER)}
                    className="form-check-input"
                  />
                  <span className="text-secondary-light">Broker</span>
                </label>
                <label className="d-flex align-items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="userType"
                    checked={userType === ROLES.USER}
                    onChange={() => setUserType(ROLES.USER)}
                    className="form-check-input"
                  />
                  <span className="text-secondary-light">User</span>
                </label>
              </div>
            </div>

            {/* User Selection */}
            <div style={{ minWidth: "180px" }}>
              <SearchableSelect
                placeholder={`Select ${userType === ROLES.BROKER ? "Broker" : "User"}`}
                value={user}
                showSearch={true}
                onChange={(e) => setUser(e.target.value)}
                options={userOptions}
              // isValueVisible={true}
              />
            </div>

            {/* Amount */}
            <div className="col-lg-6">
              <label className="form-label fw-bold text-primary-light mb-8 d-block">
                Amount (₹)
              </label>
              <div className="input-group">
                <input
                  type="number"
                  // min="0"
                  step="0.01"
                  value={amount}
                  onChange={(e) => e.target.value === "" ? setAmount("") : setAmount(Number(e.target.value) || "")}
                  className="form-control radius-12 h-40-px"
                  placeholder="0"
                />
                <button
                  type="button"
                  onClick={handleAmountUp}
                  className="btn btn-outline-secondary radius-12 h-40-px px-3"
                >
                  +
                </button>
                <button
                  type="button"
                  onClick={handleAmountDown}
                  className="btn btn-outline-secondary radius-12 h-40-px px-3"
                >
                  −
                </button>
              </div>
            </div>

            {/* Date */}
            <div className="col-lg-6">
              <label className="form-label fw-bold text-primary-light mb-8 d-block">
                Date
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="form-control radius-12 h-40-px"
              />
            </div>

            {/* Remarks */}
            <div className="col-12">
              <label className="form-label fw-bold text-primary-light mb-8 d-block">
                Remarks
              </label>
              <textarea
                placeholder="Details like Hastak, Contact, Hisab System etc."
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                className="form-control radius-12"
                rows="4"
              />
            </div>

            {/* Submit Button */}
            <div className="col-12 pt-3">
              <button
                type="submit"
                disabled={submitting || loadingOptions}
                className="btn btn-primary radius-8 px-5 py-10 fw-semibold"
                style={{ backgroundColor: "#043ba9", border: "none" }}
              >
                {submitting ? "Submitting..." : "Submit Cash Entry"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CashEntryLayer;
